import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../store/auth.store'

export interface CreateUserData {
  email: string
  password: string
  firstName: string
  lastName: string
  role: string
  areaId?: string
}

export interface UpdateUserData {
  firstName?: string
  lastName?: string
  role?: string
  areaId?: string
  isActive?: boolean
}

export function useCreateUser() {
  const api = useApi()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateUserData) => api.post('/auth/register', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-users'] })
    },
  })
}

export function useUpdateUser() {
  const api = useApi()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      api.patch(`/auth/users/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-users'] })
    },
  })
}

export function useToggleUserStatus() {
  const api = useApi()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.post(`/auth/users/${id}/toggle-status`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-users'] })
    },
  })
}

export interface AdminResetPasswordData {
  newPassword?: string
  mustChange?: boolean
}

export interface AdminResetPasswordResult {
  success: boolean
  mustChangePassword: boolean
  emailSent: boolean
  tempPassword?: string
  devTempPassword?: string
  message: string
}

export function useAdminResetPassword() {
  const api = useApi()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: AdminResetPasswordData }) =>
      api.post<AdminResetPasswordResult>(`/auth/users/${id}/reset-password`, data || {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-users'] })
    },
  })
}